import { parseKeyedMessage, renderKeyedMessage } from '../keyedMessage';
import { translateKnownKey } from '../errors/readApiError';
import type { JobStatusDto } from '../../hooks/useJobStatus';
import { JobStatus, isTerminalJobStatus } from './jobStatus';

export type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The operator-facing reading of `BackgroundJobLog.errorMessage`: localized text when EVERY line
 * is a classified `key|json` token (AB#1524/AB#1543), otherwise null. An unclassified value is a
 * raw exception message and an internal detail, so callers must never render it verbatim.
 */
export function classifiedJobReason(errorMessage: string | null | undefined, t: Translate): string | null {
  if (!errorMessage) return null;
  const allClassified = errorMessage.split('\n').every(line => parseKeyedMessage(line) !== null);
  return allClassified ? renderKeyedMessage(errorMessage, t) : null;
}

/**
 * What to show for a Failed job's `errorMessage`: the classified `key|json` reading when there
 * is one, a bare i18n key (rows written before jobs keyed their failures) through the caller's
 * `t`, and the generic server-error sentence for everything else, including a key-shaped value
 * the locale does not know. A raw exception message is an internal detail and never reaches the
 * screen.
 */
export function failedJobReason(errorMessage: string | null | undefined, t: Translate): string {
  const classified = classifiedJobReason(errorMessage, t);
  if (classified !== null) return classified;
  return (errorMessage && translateKnownKey(errorMessage, undefined, t)) || t('errors.generic.serverError');
}

/**
 * Store-scoped job lists also carry user-triggered bulk jobs (e.g. ImportMarketplaceProductMatching)
 * whose label lives under `job.bulk.type.*`, so both dictionaries are tried before the generic label.
 */
export function jobTypeLabel(jobType: string, t: Translate): string {
  return t(`marketplace.jobType.${jobType}`, {
    defaultValue: t(`job.bulk.type.${jobType}`, { defaultValue: t('job.outcome.unknownJobType') }),
  });
}

export interface JobOutcome {
  /** Finished as Completed but skipped or failed some of its items. */
  completedWithErrors: boolean;
  reason: { text: string; tone: 'destructive' | 'warning' } | null;
}

/**
 * One definition of "how did this job end" for job-result surfaces. A Completed job that carries
 * any errorMessage counts as completed-with-errors even at failedItems=0: some providers report a
 * skip only through the reason, and some price jobs return early with a raw sentence (missing
 * SupplierId), so a green "Completed" there reads as success. Classified lines are localized;
 * raw lines are internal detail and only surface as a generic warning.
 */
export function jobOutcome(
  job: Pick<JobStatusDto, 'status' | 'failedItems' | 'errorMessage'>,
  t: Translate,
): JobOutcome {
  if (!isTerminalJobStatus(job.status)) return { completedWithErrors: false, reason: null };

  const lines = (job.errorMessage ?? '').split('\n').filter(line => line.trim() !== '');
  const classifiedLines = lines.filter(line => parseKeyedMessage(line) !== null);
  const hasRawLine = classifiedLines.length < lines.length;
  const classified = classifiedLines.length > 0 ? renderKeyedMessage(classifiedLines.join('\n'), t) : null;

  if (job.status === JobStatus.Failed) {
    return {
      completedWithErrors: false,
      reason: { text: classified ?? t('errors.generic.serverError'), tone: 'destructive' },
    };
  }

  const text = [classified, hasRawLine ? t('job.outcome.unclassifiedWarning') : null]
    .filter((part): part is string => part !== null)
    .join('\n');
  const reason = text ? { text, tone: 'warning' as const } : null;
  const completedWithErrors = job.status === JobStatus.Completed && (job.failedItems > 0 || reason !== null);
  return { completedWithErrors, reason };
}
